/**
 * `createTempoMap` — Phira-exact beat <-> seconds conversion (W1-01 core-time-math; rpe-format.md F§3.2).
 *
 * Built from the BPM list IN THE GIVEN ORDER, exactly as Phira's `BpmList::new`, in f64:
 *   T_0 = 0
 *   T_i = T_{i-1} + (b_i - b_{i-1}) * (60 / bpm_{i-1})          (b_i = a + b / c of the i-th startTime)
 * Beats -> seconds: k = last index with b_k <= beats (0 if none); T_k + (beats - b_k) * (60 / bpm_k).
 * Seconds -> beats: k = last index with T_k <= t (0 if none);    b_k + (t - T_k) / (60 / bpm_k).
 * At a BPM change beat the new segment is used (b_k == beats picks segment k). Beats before the first point are
 * extrapolated with the first BPM. Every expression keeps Phira's operation order so equal beats give
 * bit-identical seconds (multi-highlight is exact).
 *
 * The editor model guarantees a sorted list starting at beat 0 with bpm > 0; lookups then binary-search. Lists in
 * any other order (imported extra.json BPM lists) fall back to the literal "last index with ..." definition.
 */

import { Search } from '../math/search';
import { beatFromF64Snapped, beatFromRatExact, Beats, U32_MAX } from './beat';
import { convergents, dyadicOf } from '../math/rational';
import type { Beat, CreateTempoMap, Sec, TempoMap, TempoPointRt, Triple } from './types';

/** Default grid for `beatAt` (DESIGN.md: free placement is still rounded to 1/1920 beat). */
export const DEFAULT_SNAP_DIV = 1920;

/** Relative tolerance of the exact path of `beatAt`: about 4 ulps of the time. */
const EXACT_REL_TOL = 2 ** -50;

function isNonDecreasing(xs: Float64Array): boolean {
  for (let i = 1; i < xs.length; i++) {
    const prev = xs[i - 1] ?? 0;
    const cur = xs[i] ?? 0;
    if (!(cur >= prev)) return false;
  }
  return true;
}

/** Last index k with xs[k] <= x, or 0 when there is none. */
function lastAtOrBefore(xs: Float64Array, sorted: boolean, x: number): number {
  if (sorted) {
    const i = Search.upperBound(xs, x) - 1;
    return i < 0 ? 0 : i;
  }
  for (let i = xs.length - 1; i >= 0; i--) {
    if ((xs[i] ?? Number.NaN) <= x) return i;
  }
  return 0;
}

export const createTempoMap: CreateTempoMap = (bpmList) => {
  const count = bpmList.length;
  if (count === 0) throw new RangeError('tempo: the BPM list must not be empty');
  const beats = new Float64Array(count);
  const times = new Float64Array(count);
  const bpms = new Float64Array(count);
  const points: TempoPointRt[] = [];
  let prevBeat = 0;
  let prevTime = 0;
  let prevBpm = 0;
  for (const [i, item] of bpmList.entries()) {
    const st = item.startTime;
    const b = st[0] + st[1] / st[2];
    const time = i === 0 ? 0 : prevTime + (b - prevBeat) * (60 / prevBpm);
    beats[i] = b;
    times[i] = time;
    bpms[i] = item.bpm;
    points.push(Object.freeze({ beat: b, time, bpm: item.bpm }));
    prevBeat = b;
    prevTime = time;
    prevBpm = item.bpm;
  }
  const beatsSorted = isNonDecreasing(beats);
  const timesSorted = isNonDecreasing(times);

  const segmentAtBeatF64 = (x: number): number => lastAtOrBefore(beats, beatsSorted, x);

  const secAtBeatF64 = (x: number): Sec => {
    const k = lastAtOrBefore(beats, beatsSorted, x);
    return (times[k] ?? 0) + (x - (beats[k] ?? 0)) * (60 / (bpms[k] ?? 0));
  };

  const beatF64At = (t: Sec): number => {
    const k = lastAtOrBefore(times, timesSorted, t);
    return (beats[k] ?? 0) + (t - (times[k] ?? 0)) / (60 / (bpms[k] ?? 0));
  };

  const secAt = (b: Beat): Sec => secAtBeatF64(Beats.toF64(b));

  /**
   * Exact Beat when a rational with denominator <= max(snapDiv, 1920) (capped at 2^32 - 1) reproduces t, else the
   * nearest multiple of 1/snapDiv. snapDiv must be a positive safe integer; above 2^32 - 1 the multiple may not be
   * representable and then falls back to the 1/80640 overflow grid (core/time/beat.ts header).
   */
  const beatAt = (t: Sec, snapDiv: number = DEFAULT_SNAP_DIV): Beat => {
    if (!Number.isFinite(t)) throw new RangeError(`tempo: not a finite time (${String(t)})`);
    if (!Number.isSafeInteger(snapDiv) || snapDiv <= 0) {
      throw new RangeError(`tempo: snapDiv must be a positive integer (got ${String(snapDiv)})`);
    }
    const x = beatF64At(t);
    if (!Number.isFinite(x)) throw new RangeError(`tempo: time ${String(t)} has no finite beat`);
    // Exact path: the smallest-denominator rational (denominator <= max(snapDiv, 1920)) whose Phira seconds
    // reproduce t (to a few ulps). Convergents are the best approximations, so the first match is the simplest.
    const tol = Math.abs(t) * EXACT_REL_TOL;
    const bound = BigInt(Math.min(Math.max(snapDiv, DEFAULT_SNAP_DIV), U32_MAX));
    for (const c of convergents(dyadicOf(x), bound)) {
      const candidate = beatFromRatExact(c);
      if (candidate !== null && Math.abs(secAt(candidate) - t) <= tol) return candidate;
    }
    return beatFromF64Snapped(x, snapDiv);
  };

  const map: TempoMap = {
    points: Object.freeze(points),
    secAtBeatF64,
    secAt,
    secAtTriple: (tr: Triple): Sec => secAtBeatF64(Beats.tripleToF64(tr)),
    beatF64At,
    beatAt,
    bpmAtBeatF64: (x: number): number => bpms[segmentAtBeatF64(x)] ?? 0,
    segmentAtBeatF64,
  };
  return Object.freeze(map);
};
